/**
 * FeatureCardView - Pure UI component for feature cards
 * 
 * This is the View layer that handles only presentation.
 * Contains no logic, hooks, or side effects.
 */
import type { ReactNode, MouseEventHandler, KeyboardEventHandler } from 'react';
import './FeatureCardView.css';

interface FeatureCardViewProps {
  title: string;
  description: string;
  icon: ReactNode;
  onMouseEnter: MouseEventHandler<HTMLDivElement>;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
  color?: string;
}

function FeatureCardView({ 
  title, 
  description, 
  icon,
  color,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  onClick
}: FeatureCardViewProps) {
  const accentColor = color ?? '#00ffff';
  
  return (
    <article
      className="feature-card-view"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      onClick={onClick}
      style={{ '--feature-card-accent-color': accentColor } as React.CSSProperties}
      role="article"
      aria-labelledby={`feature-title-${title.replace(/\s+/g, '-').toLowerCase()}`}
      aria-describedby={`feature-desc-${title.replace(/\s+/g, '-').toLowerCase()}`}
      tabIndex={0}
    >
      <div 
        className="feature-card-view-icon feature-card-icon"
        role="img"
        aria-label={`${title} icon`}
      >
        {icon}
      </div>
      <h3 
        id={`feature-title-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className="feature-card-view-title feature-card-title"
      >
        {title}
      </h3>
      <p 
        id={`feature-desc-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className="feature-card-view-description"
      >
        {description}
      </p>
    </article>
  );
}

export default FeatureCardView;
